import { Autocomplete, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RelativeDate } from './types';

export const RulesDatePicker = ({
  value,
  timezone,
  onChange,
}: {
  value: string | RelativeDate;
  timezone?: string;
  onChange: (value: { isRollingDate: boolean; value: RelativeDate | Dayjs }) => void;
}) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const { options, selectedOption } = useDatePickerRelativeOptions(datePickerVisible, value);

  const defaultDatePickerValue = useMemo(() => {
    return selectedOption === RelativeDate.CUSTOM ? dayjs(value) : null;
  }, [value, selectedOption]);

  const [datePickerValue, setDatePickerValue] = useState<Dayjs | null>(defaultDatePickerValue);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleDateChange = useCallback(
    (selectedDate: RelativeDate, datePickerValue?: Dayjs) => {
      if (selectedDate === RelativeDate.CUSTOM && datePickerValue) {
        onChangeRef.current({ isRollingDate: false, value: datePickerValue });
      } else {
        onChangeRef.current({ isRollingDate: true, value: selectedDate });
      }
    },
    [],
  );

  useEffect(() => {
    if (selectedOption && !value) {
      handleDateChange(selectedOption);
    }

    if (
      selectedOption === RelativeDate.CUSTOM &&
      value &&
      datePickerValue &&
      !datesAreEqual(dayjs(value), datePickerValue)
    ) {
      setDatePickerValue(dayjs(value));
    }
  }, [selectedOption, value, handleDateChange, datePickerValue]);

  const showDatePicker = useMemo(() => {
    return selectedOption === RelativeDate.CUSTOM || datePickerVisible;
  }, [selectedOption, datePickerVisible]);

  return (
    <>
      <Autocomplete
        size="small"
        disableClearable
        options={options}
        value={selectedOption}
        renderInput={(params) => <TextField sx={{ minWidth: '12rem' }} {...params} />}
        onChange={(_, option) => {
          if (option === RelativeDate.CUSTOM) {
            handleDateChange(option, dayjs());
          } else {
            handleDateChange(option);
          }
          setDatePickerVisible(option === RelativeDate.CUSTOM);
          setDatePickerOpen(option === RelativeDate.CUSTOM);
        }}
      />
      {showDatePicker ? (
        <DatePicker
          sx={{ marginTop: '0.25rem' }}
          open={datePickerOpen}
          value={datePickerValue}
          onOpen={() => setDatePickerOpen(true)}
          onClose={() => setDatePickerOpen(false)}
          timezone={timezone}
          slotProps={{ textField: { size: 'small' } }}
          onChange={(value) => {
            if (value) {
              setDatePickerValue(value);
              handleDateChange(RelativeDate.CUSTOM, value);
            }
          }}
        />
      ) : null}
    </>
  );
};

function useDatePickerRelativeOptions(
  datePickerVisible: boolean,
  value: RelativeDate | string,
): {
  options: RelativeDate[];
  selectedOption?: RelativeDate;
} {
  const options = useMemo(() => {
    return [
      RelativeDate.TODAY,
      RelativeDate.TOMORROW,
      RelativeDate.ONE_WEEK_FROM_NOW,
      RelativeDate.YESTERDAY,
      RelativeDate.ONE_WEEK_AGO,
      RelativeDate.THIRTY_DAYS_AGO,
      RelativeDate.SIXTY_DAYS_AGO,
      RelativeDate.NINETY_DAYS_AGO,
      RelativeDate.CUSTOM,
    ];
  }, []);

  const selectedOption = useMemo(() => {
    if (!options.length) {
      return;
    }

    const todayOption = options.find((item) => item === RelativeDate.TODAY);

    // new options
    if (!value) {
      return todayOption;
    }

    const customOption = options.find((item) => item === RelativeDate.CUSTOM);

    if (datePickerVisible) {
      return customOption;
    }

    const selectedOption = options.find((item) => item === (value as RelativeDate));
    return selectedOption || customOption;
  }, [options, value, datePickerVisible]);

  return {
    options,
    selectedOption,
  };
}

function datesAreEqual(firstDate: Dayjs, secondDate: Dayjs) {
  return firstDate?.format('DD/MM/YYYY') === secondDate?.format('DD/MM/YYYY');
}
